import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { getErrorDetails } from "../../../app/appUtils";
import AppButton from "../../../components/ui/AppButton";
import AppInputField from "../../../components/ui/AppInputField";
import { Spinner } from "../../../components/ui/common";
import { Modal } from "../../../components/ui/Modal";
import SuccessView from "../../../components/ui/SuccessView";
import { useToast } from "../../../hooks/useToast";
import { Brand } from "../@types";
import {
  useCreateBrandMutation,
  useUpdateBrandMutation,
} from "../brandApiSlice";

export const brandFormSchema = z.object({
  name: z.string().nonempty("Name is required"),
  description: z.string().nonempty("description is required"),
});

export type BrandFormData = z.infer<typeof brandFormSchema>;

interface BrandModelProps {
  isOpen: boolean;
  onClose: () => void;
  brand?: Brand | null;
  mode?: "add" | "edit";
}

const BrandModal: React.FC<BrandModelProps> = ({
  isOpen,
  onClose,
  brand = null,
  mode = brand ? "edit" : "add",
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const toast = useToast();
  const isEditMode = mode === "edit" && brand;

  const [
    createFine,
    {
      isLoading: isCreating,
      isSuccess: createSuccess,
      isError: createError,
      error: createErrorData,
      reset: resetCreate,
    },
  ] = useCreateBrandMutation();

  const [
    updateFine,
    {
      isLoading: isUpdating,
      isSuccess: updateSuccess,
      isError: updateError,
      error: updateErrorData,
      reset: resetUpdate,
    },
  ] = useUpdateBrandMutation();

  // Derived states for cleaner code
  const isLoading = isCreating || isUpdating;
  const isSuccess = createSuccess || updateSuccess;
  const isError = createError || updateError;
  const error = createErrorData || updateErrorData;

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    watch,
    setValue,
    reset,
  } = useForm<BrandFormData>({
    resolver: zodResolver(brandFormSchema),
    mode: "onChange",
    defaultValues: {
      name: "",
      description: "",
    },
  });

  // Populate form with brand data when editing
  useEffect(() => {
    if (isEditMode && brand) {
      reset({
        name: brand.name,
        description: brand.description,
      });
    } else if (!isEditMode) {
      // Reset to empty when switching to add mode
      reset({
        name: "",
        description: "",
      });
    }
  }, [isEditMode, brand, reset]);

  const onSubmit = async (data: BrandFormData) => {
    if (isSubmitting || isLoading) return;

    // Prevent submission if editing and no changes were made
    if (isEditMode && !isDirty) {
      toast.warning({
        title: "No Changes",
        message: "No changes were made to update.",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        name: data.name,
        description: data.description,
      };

      if (isEditMode && brand?.id) {
        await updateFine({
          id: brand.id,
          ...payload,
        }).unwrap();
      } else {
        await createFine(payload).unwrap();
      }

      setShowSuccess(true);
    } catch (err) {
      console.error(`${isEditMode ? "Update" : "Create"} error:`, err);
      const errorMessage =
        getErrorDetails(err).message ||
        `Failed to ${isEditMode ? "update" : "add"} brand. Please try again.`;

      toast.error({
        title: "Error",
        message: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (isSuccess && !showSuccess) {
      toast.success({
        title: "Success",
        message: `Brand ${isEditMode ? "updated" : "added"} successfully`,
      });
      setShowSuccess(true);
    }

    if (isError) {
      const errorMessage =
        getErrorDetails(error).message ||
        `Failed to ${isEditMode ? "update" : "add"} brand. Please try again.`;

      toast.error({
        title: "Error",
        message: errorMessage,
      });
    }
  }, [isSuccess, isError, error, toast, isEditMode, showSuccess]);

  const handleCloseModal = () => {
    setShowSuccess(false);
    reset();
    resetCreate();
    resetUpdate();
    onClose();
  };

  const renderSuccessView = () => {
    if (showSuccess) {
      return (
        <SuccessView
          handleCloseModal={handleCloseModal}
          heading=""
          message={`Brand has been ${
            isEditMode ? "updated" : "added"
          } successfully.`}
        />
      );
    }
    return null;
  };

  const modalTitle = showSuccess ? "" : `${isEditMode ? "Edit" : "Add"} brand`;
  const submitButtonText = isEditMode ? "Update brand" : "+ Add brand";

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCloseModal}
      title={modalTitle}
      className="max-w-full sm:max-w-[80vw] lg:max-w-[45vw] w-full bg-white"
    >
      {isSuccess ? (
        renderSuccessView()
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 p-6">
          <div className="">
            <Controller
              control={control}
              name="name"
              render={({ field }) => (
                <AppInputField
                  label="Enter name"
                  type="text"
                  error={errors.name?.message}
                  {...field}
                  className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                  placeholder="Enter name"
                />
              )}
            />
          </div>

          <div className="">
            <Controller
              control={control}
              name="description"
              render={({ field }) => (
                <AppInputField
                  label="Enter description"
                  type="text"
                  error={errors.description?.message}
                  {...field}
                  className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                  placeholder="Enter description"
                />
              )}
            />
          </div>

          <div className="flex justify-center items-center space-x-4 text-[15px]">
            <AppButton
              type="button"
              onClick={handleCloseModal}
              disabled={isSubmitting || isLoading}
              className="px-6 py-2 bg-[#F6F6F6] rounded-md text-hColor hover:bg-gray-50 transition-colors text-[15px] disabled:opacity-50"
            >
              Cancel
            </AppButton>

            <AppButton
              type="submit"
              disabled={isSubmitting || isLoading}
              className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary/75 transition-colors disabled:opacity-50 text-[15px]"
            >
              {isSubmitting || isLoading ? (
                <>
                  <Spinner /> Adding
                </>
              ) : (
                submitButtonText
              )}
            </AppButton>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default BrandModal;
